if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      // Convert the form into the items[] shape submitItems() expects so a
      // single-product add and a multi-item bundle add share one path.
      // Only one level of properties[key] is unwrapped here; nested keys
      // (properties[Custom][Color]) or file inputs would not survive.
      const formData = new FormData(this.form);
      const items = [
        {
          id: formData.get('id'),
          quantity: parseInt(formData.get('quantity'), 10) || 1,
        },
      ];
      for (const [key, value] of formData.entries()) {
        if (key.startsWith('properties[') && key.endsWith(']')) {
          items[0].properties = items[0].properties || {};
          items[0].properties[key.slice(11, -1)] = value;
        }
      }

      try {
        const data = await ProductForm.submitItems(items);

        if (data.status) this.setErrorMsgState(data.description || data.message);

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.setErrorMsgState(error.cartError || error.message);
        this.dispatchEvent(
          new CustomEvent('on:cart:error', {
            bubbles: true,
            detail: {
              error: this.errorMsg?.textContent,
            },
          })
        );

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Shared add-to-cart for one or more line items (native product form,
     * sticky bar, bundle widget, quick add). Single source of truth for the
     * /cart/add.js request, dataLayer/attribution pushes, and cart events.
     * Do not fetch('/cart/add.js') anywhere else in the theme.
     *
     * @param {Array<{id:string|number, quantity:number, properties?:object}>} items
     * @returns {Promise<object>} the /cart/add.js response (has .items[] for a
     *   multi-item add, or the single item's fields for a one-item add).
     */
    static async submitItems(items) {
      const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
      if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
      const oldCartData = await oldCartResponse.json();

      let sections = 'cart-icon-bubble';
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        const cartDrawerSection = cartDrawer.closest('.shopify-section');
        if (cartDrawerSection) {
          sections += `,${cartDrawerSection.id.replace('shopify-section-', '')}`;
        }
      }

      const response = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items,
          sections_url: window.location.pathname,
          sections,
        }),
      });

      const data = await response.json();
      let error = typeof data.description === 'string' ? data.description : data.message;
      if (data.errors && typeof data.errors === 'object') {
        error = Object.entries(data.errors).map((item) => item[1].join(', '));
      }

      if (!response.ok) {
        // Thrown as a real Error with .cartError so every caller (this form and
        // bundle-widget.js) can read the same parsed Shopify message.
        const err = new Error(Array.isArray(error) ? error.join(', ') : error || `HTTP ${response.status}`);
        err.cartError = error;
        throw err;
      }

      // Shopify returns { items: [...] } for multi-item adds, or the single
      // item's own fields at the top level for exactly one item.
      const addedItems = Array.isArray(data.items) ? data.items : [data];

      // Per added item so bundle accessories are tracked too, not just the main product.
      addedItems.forEach((item) => {
        ProductForm.pushAddToCartEvent(item, {
          get: (key) => (key === 'quantity' ? item.quantity : null),
        });
        ProductForm.saveCartAttribution(item.product_id);
      });

      const newCartResponse = await fetch(`${theme.routes.cart}.js`);
      if (!newCartResponse.ok) throw new Error(newCartResponse.status);
      const newCartData = await newCartResponse.json();

      addedItems.forEach((item) => {
        const itemInOldCart = oldCartData.items.filter((oi) => oi.variant_id === item.variant_id)[0];

        if (itemInOldCart) {
          document.dispatchEvent(
            new CustomEvent('on:line-item:change', {
              bubbles: true,
              detail: {
                cart: newCartData,
                variantId: item.variant_id,
                oldQuantity: itemInOldCart.quantity,
                newQuantity: itemInOldCart.quantity === item.quantity ? itemInOldCart.quantity : item.quantity,
              },
            })
          );
        } else {
          document.dispatchEvent(
            new CustomEvent('on:cart:add', {
              bubbles: true,
              detail: {
                cart: newCartData,
                variantId: item.variant_id,
              },
            })
          );
        }
      });

      return data;
    }

    /**
     * Look up the Cloud Retail attribution token for a product id (per-search-response map).
     * @param {string|number} productId
     * @returns {string|null}
     */
    static getAttributionToken(productId) {
      if (window.CloudRetailAttribution) {
        return window.CloudRetailAttribution.getToken(productId);
      }
      return null;
    }

    static getCloudRetailRegion() {
      if (window.CloudRetailAttribution) {
        return window.CloudRetailAttribution.getRegion();
      }
      return '';
    }

    /**
     * First-party visitor id. Prefers the shared CloudRetailAttribution source
     * (minted on page load); falls back to reading/minting the cookie directly
     * so add-to-cart never runs without a visitor id.
     */
    static getVisitorId() {
      if (window.CloudRetailAttribution && window.CloudRetailAttribution.getVisitorId) {
        return window.CloudRetailAttribution.getVisitorId();
      }
      const match = document.cookie.match(/(?:^|; )cloud_retail_visitor_id=([^;]*)/);
      if (match) return decodeURIComponent(match[1]);
      const id = (window.crypto && crypto.randomUUID)
        ? crypto.randomUUID()
        : 'v-' + Date.now() + '-' + Math.random().toString(36).slice(2);
      document.cookie = 'cloud_retail_visitor_id=' + encodeURIComponent(id) + '; path=/; max-age=63072000; SameSite=Lax';
      return id;
    }

    /**
     * Cloud Retail add-to-cart event. Fires for every add; attributionToken is
     * included only when this product was attributed from AI search.
     * @param {object} data - /cart/add line item
     * @param {{get:Function}} formData - real FormData or a { get } shim
     */
    static pushAddToCartEvent(data, formData) {
      if (!data || !data.product_id) return;

      const attributionToken = ProductForm.getAttributionToken(data.product_id);
      const region = ProductForm.getCloudRetailRegion();
      const entity = region && region.toLowerCase();
      const qty = parseInt(formData.get('quantity') || data.quantity || '1', 10) || 1;

      window.dataLayer = window.dataLayer || [];
      // Clear nested cloud_retail so GTM does not merge leftover search productDetails / searchQuery.
      window.dataLayer.push({ cloud_retail: null });
      window.dataLayer.push({
        event: 'cloud_retail_add_to_cart',
        cloud_retail: {
          eventType: 'add-to-cart',
          eventTime: new Date().toISOString(),
          visitorId: ProductForm.getVisitorId(),
          ...(attributionToken && { attributionToken }),
          productDetails: [
            {
              product: { id: String(data.product_id) },
              quantity: { value: qty },
            },
          ],
          ...(region && {
            entity,
            attributes: {
              market: { text: [region] },
            },
          }),
          uri: location.href,
        },
      });
    }

    /**
     * After ATC only: persist Cloud Retail fields as cart attributes for the
     * checkout pixel. Never call on page load (empty-cart updates are dropped).
     * - cloud_retail_visitor_id (theme-minted first-party cookie)
     * - cloud_retail_attribution_token (when this product was AI-attributed)
     */
    static saveCartAttribution(productId) {
      const attributes = {};
      const visitorId = ProductForm.getVisitorId();
      if (visitorId) attributes.cloud_retail_visitor_id = visitorId;

      const attributionToken = productId ? ProductForm.getAttributionToken(productId) : null;
      if (attributionToken) attributes.cloud_retail_attribution_token = attributionToken;

      if (!Object.keys(attributes).length) return Promise.resolve();

      return fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ attributes }),
      }).catch((err) => {
        console.error('[cloud_retail] failed to save cart attributes', err);
      });
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble && response.sections) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
  window.saveCloudRetailCartAttribution = (productId) => ProductForm.saveCartAttribution(productId);
  window.pushCloudRetailAddToCartEvent = (data, formData) =>
    ProductForm.pushAddToCartEvent(data, formData);
}
